'use client';

import { useLondonNow } from '@/components/chrome/useLondonNow';
import { SITE_COPY } from '@/lib/data/copy';
import { DAY_KEYS, type DayKey } from '@/lib/content';
import { formatPrice } from '@/lib/price';
import { SHOP } from '@/lib/data/shop';
import { isTrading } from '@/lib/opening';

const DAY_NAME: Record<DayKey, string> = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
};

/** The London weekday, so a special changes at midnight in Aldershot. */
export function londonDay(now: Date): DayKey {
  const name = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    timeZone: 'Europe/London',
  })
    .format(now)
    .toLowerCase() as DayKey;
  return DAY_KEYS.includes(name) ? name : 'sunday';
}

/**
 * A five-pointed star in brick: straight edges only, so it keeps to the rule
 * that nothing on the site is rounded, and one colour, the accent.
 */
function Star({ className = '' }: { className?: string }) {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className={className} fill="currentColor">
      <polygon points="12,1.5 15.1,8.6 22.8,9.3 17,14.4 18.7,22 12,18.1 5.3,22 7,14.4 1.2,9.3 8.9,8.6" />
    </svg>
  );
}

/**
 * Today's special, from Words and photos. The clock is read in the browser,
 * the same way the open status is, because the page is static and a special
 * baked in at build would be yesterday's by breakfast. Until the clock has
 * ticked, and on any day left blank in the editor, nothing renders.
 */
export function TodaysSpecial({ className = '' }: { className?: string }) {
  const now = useLondonNow();
  const specials = SITE_COPY.specials;
  if (!now || !specials || !isTrading(now, SHOP)) return null;
  const day = londonDay(now);
  const special = specials.days[day];
  if (!special) return null;

  return (
    <aside
      aria-label={`${specials.label}, ${DAY_NAME[day]}`}
      className={`flex flex-wrap items-center gap-x-8 gap-y-3 border-y border-ink/20 bg-paper-2 px-6 py-6 md:px-9 ${className}`}
    >
      <Star className="size-5 shrink-0 text-brick" />
      <div className="flex min-w-0 flex-1 flex-wrap items-baseline gap-x-6 gap-y-1">
        <p className="label-xs text-brick">
          {specials.label} · {DAY_NAME[day]}
        </p>
        <p className="font-display text-[24px] leading-[1.2]">
          {special.name}
          <span className="ml-3 text-ink-soft">{formatPrice(special.pricePence)}</span>
        </p>
        {special.description && (
          <p className="basis-full text-[13px] leading-[1.7] text-ink-soft md:basis-auto">
            {special.description}
          </p>
        )}
      </div>
    </aside>
  );
}
